import React, { useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatMoney } from '../shared/formatting';

interface Recommendation { method: string; cash_part: number; virman_part: number; mandatory_virman: boolean; aml_report: boolean; cash_cap: number; reasons: string[] }
interface VirmanPreview { payload: string; barcode: string | null; iban: string; model: string; reference: string; aml_report: boolean }

const METHOD_META: Record<string, { hr: string; en: string; color: string; icon: string }> = {
    cash: { hr: 'GOTOVINA', en: 'CASH', color: '#1f7d4d', icon: '💵' },
    hybrid: { hr: 'HIBRID (gotovina + virman)', en: 'HYBRID (cash + wire)', color: '#a07a2f', icon: '🔀' },
    virman: { hr: 'VIRMAN (obavezno)', en: 'WIRE (mandatory)', color: '#b0341f', icon: '🏦' },
};

export function BuybackPayoutModule({ token, locale, onBack, embedded }: { token: string | null; locale: 'hr' | 'en'; onBack: () => void; embedded?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [amount, setAmount] = useState('');
    const [register, setRegister] = useState('');
    const [rec, setRec] = useState<Recommendation | null>(null);
    const [thresholds, setThresholds] = useState<{ mandatory_virman_eur: number; cash_soft_max_eur: number } | null>(null);
    const [error, setError] = useState('');
    const [companies, setCompanies] = useState<{ id: number; code: string }[]>([]);
    const [companyId, setCompanyId] = useState('');
    const [clientName, setClientName] = useState('');
    const [clientIban, setClientIban] = useState('');
    const [clientCity, setClientCity] = useState('');
    const [preview, setPreview] = useState<VirmanPreview | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // companies for the virman payer dropdown (reuse foundation if available, else a light call)
        (async () => {
            try {
                const res = await api<{ companies: { id: number; code: string }[] }>('/contracts', token).catch(() => null);
                if (res?.companies) { setCompanies(res.companies); setCompanyId((c) => c || String(res.companies[0]?.id ?? '')); }
            } catch { /* optional */ }
        })();
    }, [token]);

    async function advise() {
        if (!amount) return;
        setError(''); setPreview(null);
        try {
            const res = await mutate<{ recommendation: Recommendation; thresholds: typeof thresholds }>('/buyback-payout/advise', token, 'POST', {
                amount_eur: Number(amount), register_available: register ? Number(register) : null,
            });
            setRec(res.recommendation); setThresholds(res.thresholds);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }

    async function genVirman() {
        if (!companyId || !clientName || !clientIban) { setError(t('Tvrtka, ime i IBAN klijenta su obavezni.', 'Company, client name and IBAN required.')); return; }
        setBusy(true); setError('');
        try {
            const res = await mutate<VirmanPreview>('/buyback-payout/virman-preview', token, 'POST', {
                company_id: Number(companyId),
                amount_eur: rec?.method === 'hybrid' ? rec.virman_part : Number(amount),
                client_name: clientName, client_iban: clientIban, client_city: clientCity || null,
            });
            setPreview(res);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); } finally { setBusy(false); }
    }

    const meta = rec ? METHOD_META[rec.method] : null;

    return (
        <div style={{ padding: '18px 20px', color: '#2f3337', maxWidth: 720 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                {!embedded ? <button onClick={onBack} style={btnGhost}>← {t('Natrag', 'Back')}</button> : null}
                <h2 style={{ margin: 0, fontSize: 20 }}>💵 {t('Isplata otkupa klijentu', 'Buyback payout')}</h2>
            </div>

            {error ? <div style={{ background: '#fdecec', color: '#b3261e', padding: 10, borderRadius: 8, marginBottom: 12 }}>{error}</div> : null}

            <div style={{ border: '1px solid #efe6d2', borderRadius: 10, padding: 16, background: '#fbf7ee', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div><label style={lbl}>{t('Iznos isplate (€)', 'Payout amount (€)')}</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...input, width: 160 }} /></div>
                    <div><label style={lbl}>{t('Stanje blagajne (€, opc.)', 'Register cash (€, opt.)')}</label><input type="number" value={register} onChange={(e) => setRegister(e.target.value)} placeholder={t('nepoznato', 'unknown')} style={{ ...input, width: 160 }} /></div>
                    <button onClick={() => void advise()} style={{ ...btnGhost, color: '#fff', background: '#2f6aa3', borderColor: '#2f6aa3' }}>{t('Preporuči', 'Recommend')}</button>
                </div>
                {thresholds ? <div style={{ fontSize: 11, opacity: 0.5, marginTop: 8 }}>{t('AML obavezan virman', 'AML mandatory wire')} ≥ {formatMoney(thresholds.mandatory_virman_eur, locale)} · {t('gotovinski limit', 'cash cap')} {formatMoney(thresholds.cash_soft_max_eur, locale)}</div> : null}
            </div>

            {rec && meta ? (
                <div style={{ border: `1px solid ${meta.color}`, borderRadius: 10, padding: 16, marginBottom: 16, background: '#ffffff' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: meta.color }}>{meta.icon} {locale === 'en' ? meta.en : meta.hr}</div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                        {rec.cash_part > 0 ? <div>💵 {t('Gotovina', 'Cash')}: <b>{formatMoney(rec.cash_part, locale)}</b></div> : null}
                        {rec.virman_part > 0 ? <div>🏦 {t('Virman', 'Wire')}: <b>{formatMoney(rec.virman_part, locale)}</b></div> : null}
                        {rec.aml_report ? <div style={{ color: '#c0392b' }}>⚠ {t('SPNFT prijava', 'AML report')}</div> : null}
                    </div>
                    <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, opacity: 0.85 }}>
                        {rec.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                </div>
            ) : null}

            {rec && rec.virman_part > 0 ? (
                <div style={{ border: '1px solid #efe6d2', borderRadius: 10, padding: 16, background: '#fbf7ee' }}>
                    <div style={{ fontWeight: 600, marginBottom: 10 }}>🏦 {t('Virman klijentu', 'Client wire')} ({formatMoney(rec.virman_part, locale)})</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={input}>
                            <option value="">{t('Platitelj (tvrtka)', 'Payer (company)')}</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                        </select>
                        <input value={clientCity} onChange={(e) => setClientCity(e.target.value)} placeholder={t('Grad klijenta', 'Client city')} style={input} />
                        <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder={t('Ime i prezime klijenta', 'Client name')} style={input} />
                        <input value={clientIban} onChange={(e) => setClientIban(e.target.value)} placeholder="IBAN klijenta" style={input} />
                    </div>
                    <button disabled={busy} onClick={() => void genVirman()} style={{ ...btnGhost, color: '#fff', background: '#1f7d4d', borderColor: '#1f7d4d', marginTop: 10 }}>{busy ? '…' : t('Generiraj HUB3 virman', 'Generate HUB3 wire')}</button>

                    {preview ? (
                        <div style={{ display: 'flex', gap: 16, marginTop: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {preview.barcode ? <img src={preview.barcode} alt="HUB3" style={{ width: 220, background: '#fff', borderRadius: 6, padding: 4 }} /> : <div style={{ fontSize: 12, opacity: 0.6 }}>{t('(QR se prikazuje na serveru — bcmath)', '(QR renders on server — bcmath)')}</div>}
                            <div style={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f4efe3', padding: 10, borderRadius: 8, flex: 1, minWidth: 220 }}>
                                IBAN: {preview.iban}{'\n'}Model: {preview.model} {preview.reference}{'\n'}{preview.aml_report ? '⚠ SPNFT prijava obavezna' : ''}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

const input: React.CSSProperties = { background: '#fbf7ee', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 10px', fontSize: 13 };
const btnGhost: React.CSSProperties = { background: '#fffdf6', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
const lbl: React.CSSProperties = { fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 };
